// v5.44 — Foreplay harvest job modal (docs/superpowers/plans/2026-08-11-
// foreplay-brand-harvester.md). Brand directory search (cache-first; the
// paid searches and analytics run on demand), filters and per-brand caps.
import { useRef, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Loader2, X, Search, Globe, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ForeplayProviderBalance } from "./foreplay-provider-balance";
import { createForeplayExpansionApi, createRequestKey } from "@/components/admin/foreplay-expansion-api";

interface CachedBrand {
  id: number;
  foreplayId: string;
  name: string;
  avatarUrl: string | null;
  category: string | null;
  niches: string[] | null;
  websites: string[] | null;
  metaPageId: string | null;
  lastAnalytics: { activeCount: number; image: number; video: number; carousel: number } | null;
}

export interface ForeplayHarvestFilters {
  displayFormat: "image" | "video" | "carousel";
  live?: boolean;
  minDaysRunning?: number;
  order: "newest" | "oldest" | "longest_running" | "most_relevant";
  capPerBrand: number;
}

export function SeederForeplayJobModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (jobId: number) => void;
}) {
  const [query, setQuery] = useState("");
  const [domain, setDomain] = useState("");
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CachedBrand[]>([]);
  const [creating, setCreating] = useState(false);
  // v5.45 — default order is NEWEST: with a min-days floor it means "fresh
  // creative that already survived" — current-generation ad styles (Dan
  // compared against Foreplay's own UI; longest_running surfaced 550-day-old
  // visuals that read dated). Longest-running stays selectable for
  // evergreen digs.
  const [filters, setFilters] = useState<ForeplayHarvestFilters>({
    displayFormat: "image",
    minDaysRunning: 30,
    order: "newest",
    capPerBrand: 100,
  });
  const [liveOnly, setLiveOnly] = useState(false);
  const [analyticsBusy, setAnalyticsBusy] = useState<Set<string>>(new Set());
  const [nicheFilter, setNicheFilter] = useState("");
  const requestIntent = useRef<{ signature: string; key: string } | null>(null);
  const api = createForeplayExpansionApi();


  // Cache browse — free, refires as you type.
  const { data: cacheData, refetch: refetchCache } = useQuery<{ brands: CachedBrand[] }>({
    queryKey: ["/api/admin/foreplay-harvest/brands", query],
    queryFn: async () => {
      const res = await fetch(`/api/admin/foreplay-harvest/brands?query=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error("cache search failed");
      return res.json();
    },
  });
  const [discoveryNiche, setDiscoveryNiche] = useState("");
  const [discoveryTarget, setDiscoveryTarget] = useState<10 | 20>(10);
  const [officialWebsites, setOfficialWebsites] = useState("");
  const [discoveryMessage, setDiscoveryMessage] = useState<string | null>(null);
  const [discoveryEvidence, setDiscoveryEvidence] = useState<Awaited<ReturnType<typeof api.discover>>["candidates"]>([]);
  const [discoveredNiches, setDiscoveredNiches] = useState<Record<string, string>>({});
  const discover = async () => {
    setSearching(true); setError(null); setDiscoveryMessage(null); setDiscoveryEvidence([]);
    try {
      const websites = officialWebsites.split(/[\s,]+/).filter(Boolean);
      const data = await api.discover(discoveryNiche.trim(), discoveryTarget, websites.length ? websites : undefined);
      setFreshResults(data.brands);
      setDiscoveryEvidence(data.candidates);
      setDiscoveredNiches(previous => ({ ...previous, ...Object.fromEntries(data.brands.map(brand => [brand.foreplayId, data.niche])) }));
      setDiscoveryMessage(`${data.brands.length} of ${data.target} new brands matched by website; ${data.lookups} Foreplay lookups. ${data.message ?? "Review niche fit before adding."}${data.errors.length ? " Some web searches failed." : ""}`);
    } catch (err: any) { setError(err.message || "Discovery failed"); }
    finally { setSearching(false); }
  };
  const [freshResults, setFreshResults] = useState<CachedBrand[] | null>(null);
  const allResults = freshResults ?? cacheData?.brands ?? [];
  // Distinct niche labels across the directory for the filter dropdown.
  const nicheOptions = Array.from(new Set(allResults.flatMap((b) => [discoveredNiches[b.foreplayId], b.category, ...(b.niches ?? [])]).filter((n): n is string => !!n && n !== "Service Business"))).sort();
  const coverageByNiche = useQueries({ queries: nicheOptions.map((niche) => ({
    queryKey: ["foreplay-approved-coverage", niche], queryFn: () => api.coverage(niche), staleTime: 60_000,
  })) });
  const coverageCounts = new Map(nicheOptions.map((niche, index) => [niche, coverageByNiche[index]?.data?.approvedCount]));
  const results = (nicheFilter && freshResults === null
    ? allResults.filter((b) => b.category === nicheFilter || (b.niches ?? []).includes(nicheFilter))
    : allResults).slice().sort((a, b) => {
      const coverage = (brand: CachedBrand) => {
        const niche = discoveredNiches[brand.foreplayId] || (brand.niches ?? []).find((n) => n && n !== "Service Business") || brand.category;
        return niche ? coverageCounts.get(niche) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
      };
      return coverage(a) - coverage(b) || a.name.localeCompare(b.name);
    });

  const billedSearch = async (kind: "name" | "domain") => {
    setSearching(true);
    setError(null);
    setDiscoveryEvidence([]);
    setDiscoveryMessage(null);
    try {
      const url = kind === "name"
        ? `/api/admin/foreplay-harvest/brands?query=${encodeURIComponent(query)}&fresh=1`
        : `/api/admin/foreplay-harvest/brands/domain?domain=${encodeURIComponent(domain)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error((await res.json()).message || "Search failed");
      const data = await res.json();
      setFreshResults(data.brands ?? []);
      await refetchCache();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  };

  const fetchAnalytics = async (b: CachedBrand) => {
    setAnalyticsBusy((s) => new Set(s).add(b.foreplayId));
    try {
      const res = await fetch(`/api/admin/foreplay-harvest/brands/${b.foreplayId}/analytics`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `Could not load ad counts (${res.status}).`);
      }
      const { analytics } = await res.json();
      const applyAnalytics = (brands: CachedBrand[]) => brands.map((brand) =>
        brand.foreplayId === b.foreplayId ? { ...brand, lastAnalytics: analytics } : brand,
      );
      setFreshResults((brands) => brands === null ? null : applyAnalytics(brands));
      setSelected(applyAnalytics);
      await refetchCache();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load ad counts.");
    } finally {
      setAnalyticsBusy((s) => { const n = new Set(s); n.delete(b.foreplayId); return n; });
    }
  };

  const createJob = async () => {
    setCreating(true);
    setError(null);
    try {
      const fields = {
          brands: selected.map((b) => ({
            foreplayId: b.foreplayId,
            name: b.name,
            metaPageId: b.metaPageId,
            // v5.47 — Foreplay tags half its brands "Service Business" as a
            // catch-all (RYZE = ["Service Business","Food/Drink"]); prefer
            // the real niche when one exists.
            niche: discoveredNiches[b.foreplayId] || (b.niches ?? []).find((n) => n && n !== "Service Business")
              || b.category
              || b.niches?.[0]
              || undefined,
          })),
          filters: { ...filters, live: liveOnly ? true : undefined },
        };
      const signature = JSON.stringify(fields);
      if (!requestIntent.current || requestIntent.current.signature !== signature) {
        requestIntent.current = { signature, key: createRequestKey() };
      }
      const payload = { ...fields, requestKey: requestIntent.current.key };
      const result = await api.create(payload);
      onCreated(result.job.id);
    } catch (e: any) {
      setError(e.message);
      setCreating(false);
    }
  };

  const maxAds = selected.length * filters.capPerBrand;
  const isSelected = (id: string) => selected.some((b) => b.foreplayId === id);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        data-testid="foreplay-job-modal"
        className="bg-white rounded-xl max-w-4xl w-full h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#DCE3ED] flex items-center justify-between bg-white shrink-0">
          <div>
            <h3 className="font-semibold">New Foreplay Harvest</h3>
            <p className="text-xs text-[#526074] mt-0.5">
              Pick brands → pull their top ads into Ad Review → approve keepers into Winning Ads.
            </p>
          </div>
          <button onClick={onClose} className="text-[#526074] hover:text-gray-800"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 space-y-4 flex flex-col flex-1 min-h-0 overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-2.5 rounded shrink-0">{error}</div>
          )}

          <ForeplayProviderBalance />
          <div className="space-y-2 shrink-0 border rounded-lg p-3">
            <p className="font-medium">Discover new brands by niche</p>
            <p className="text-sm text-muted-foreground">Search the web, then match official domains in Foreplay. This uses billed domain lookups; it does not harvest ads.</p>
            <div className="flex gap-2">
              <input className="border rounded px-3 py-2 flex-1" aria-label="Discovery niche" placeholder="e.g. home security" value={discoveryNiche} onChange={event => setDiscoveryNiche(event.target.value)} />
              <select aria-label="Discovery target" value={discoveryTarget} onChange={event => setDiscoveryTarget(Number(event.target.value) as 10 | 20)}><option value={10}>10 brands</option><option value={20}>20 brands</option></select>
              <Button onClick={discover} disabled={searching || discoveryNiche.trim().length < 2}>Discover brands</Button>
            </div>
            <details className="text-sm">
              <summary>Use official websites instead of web search</summary>
              <label className="mt-2 block">Official websites (optional)
                <textarea aria-label="Official websites" className="mt-1 w-full rounded border px-3 py-2" rows={3} value={officialWebsites} onChange={event => setOfficialWebsites(event.target.value)} placeholder="One website per line" disabled={searching} />
              </label>
              <p>Up to {discoveryTarget * 3} websites. Uses billed Foreplay domain lookups, skips web search, and keeps the selected niche. Review each match before adding it.</p>
            </details>
            {discoveryMessage && <p role="status" className="text-sm">{discoveryMessage}</p>}
            {discoveryEvidence.length > 0 && <details className="text-sm">
              <summary>Review discovery sources and match results ({discoveryEvidence.length})</summary>
              <div className="max-h-64 overflow-y-auto space-y-3 pt-2">
                {discoveryEvidence.map(candidate => <div key={candidate.domain} className="rounded border p-2">
                  <strong>{candidate.domain}</strong> · {candidate.status}
                  {candidate.message && <p>{candidate.message}</p>}
                  {candidate.evidence.map(source => <p key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="underline">{source.title || source.url}</a></p>)}
                </div>)}
              </div>
              <p>Website matches identify brands; review each brand’s niche fit before adding it.</p>
            </details>}
          </div>
          {/* Search */}
          <div className="grid grid-cols-2 gap-3 shrink-0">
            <div className="flex gap-2">
              <input
                data-testid="input-foreplay-brand-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Brand name (browses our cache free)"
                className="flex-1 border border-[#DCE3ED] rounded px-3 py-1.5 text-sm"
              />
              <Button size="sm" variant="outline" disabled={!query.trim() || searching} onClick={() => billedSearch("name")}>
                {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                <span className="ml-1">Search Foreplay (~10cr)</span>
              </Button>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="input-foreplay-domain"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="Or exact domain: ryzesuperfoods.com"
                className="flex-1 border border-[#DCE3ED] rounded px-3 py-1.5 text-sm"
              />
              <Button size="sm" variant="outline" disabled={!domain.trim() || searching} onClick={() => billedSearch("domain")}>
                <Globe className="h-3.5 w-3.5" /><span className="ml-1">Lookup</span>
              </Button>
            </div>
          </div>

          {/* Directory — the full cached brand index, browsing is free. */}
          <div className="flex items-center justify-between shrink-0">
            <div className="text-xs text-[#526074]">
              {results.length.toLocaleString()} brand{results.length === 1 ? "" : "s"} {freshResults !== null ? "in these fresh Foreplay results — select brands below" : `in our cached directory (not the Foreplay catalog)${nicheFilter ? ` · ${nicheFilter}` : ""} — browsing is free, typing filters instantly`}
            {freshResults !== null && <Button variant="outline" size="sm" onClick={() => { setFreshResults(null); setDiscoveryEvidence([]); setDiscoveryMessage(null); }}>Show cached brands</Button>}
            </div>
            <select
              data-testid="select-directory-niche"
              className="border border-[#DCE3ED] rounded px-2 py-1 text-xs"
              disabled={freshResults !== null} value={freshResults !== null ? "" : nicheFilter}
              onChange={(e) => setNicheFilter(e.target.value)}
            >
              <option value="">All cached niches</option>
              {nicheOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="shrink-0 max-h-32 overflow-y-auto rounded bg-indigo-50 px-3 py-2 text-xs text-indigo-950">
            Approved ads by niche guide brand ordering (lowest coverage first). Thin niches are below 20; target is 20+ approved ads.
            <div className="mt-1 flex flex-wrap gap-2">{nicheOptions.map((niche) => {
              const count = coverageCounts.get(niche);
              return <span key={niche} className="rounded bg-white px-2 py-1">{niche}: {count == null ? "loading" : `${count} · ${count === 0 ? "none yet" : count < 20 ? "below target" : "target met"}`}</span>;
            })}</div>
          </div>
          <div className="border border-[#DCE3ED] rounded divide-y divide-gray-100 flex-1 min-h-48 overflow-y-auto">
            {results.length === 0 ? (
              <div className="p-4 text-sm text-[#526074]">
                {freshResults !== null ? "No matching brands returned. Try another niche, brand name, or official domain." : <>Nothing cached{query ? ` for “${query}”` : ""} yet — use “Search Foreplay” or a domain lookup (paid requests use Foreplay credits).</>}
              </div>
            ) : results.map((b) => (
              <div key={b.foreplayId} data-testid={`foreplay-brand-row-${b.foreplayId}`} className="flex items-center gap-3 p-2.5">
                {b.avatarUrl
                  ? <img src={b.avatarUrl} alt="" className="h-8 w-8 rounded object-cover border border-[#DCE3ED]" onError={(e) => { (e.target as HTMLImageElement).style.visibility = "hidden"; }} />
                  : <div className="h-8 w-8 rounded bg-gray-100" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{b.name}</div>
                  <div className="text-[11px] text-[#526074] truncate">
                    {[b.category, ...(b.niches ?? [])].filter(Boolean).join(" · ") || "—"}
                    {(() => { const niche = discoveredNiches[b.foreplayId] || (b.niches ?? []).find((n) => n && n !== "Service Business") || b.category; const count = niche ? coverageCounts.get(niche) : undefined; return count == null ? null : ` · ${count} approved`; })()}
                    {b.websites?.[0] ? ` · ${b.websites[0].replace(/^https?:\/\//, "")}` : ""}
                  </div>
                </div>
                {b.lastAnalytics ? (
                  <div className="text-[11px] text-[#526074] text-right shrink-0">
                    <div className="font-medium">{b.lastAnalytics.activeCount.toLocaleString()} live</div>
                    <div>{b.lastAnalytics.image.toLocaleString()} img · {b.lastAnalytics.video.toLocaleString()} vid</div>
                  </div>
                ) : (
                  <button
                    className="text-[11px] text-indigo-600 hover:underline shrink-0 disabled:opacity-50"
                    disabled={analyticsBusy.has(b.foreplayId)}
                    onClick={() => fetchAnalytics(b)}
                  >
                    {analyticsBusy.has(b.foreplayId) ? "…" : "ad counts"}
                  </button>
                )}
                {isSelected(b.foreplayId) ? (
                  <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded shrink-0">Added</span>
                ) : (
                  <Button size="sm" variant="outline" className="shrink-0" data-testid={`button-add-brand-${b.foreplayId}`} onClick={() => setSelected((s) => s.some((item) => item.foreplayId === b.foreplayId) ? s : [...s, b])}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Basket */}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 shrink-0 max-h-20 overflow-y-auto">
              {selected.map((b) => (
                <span key={b.foreplayId} className="inline-flex items-center gap-1.5 text-xs bg-gray-100 rounded-full pl-3 pr-1.5 py-1">
                  {b.name}
                  <button onClick={() => setSelected((s) => s.filter((x) => x.foreplayId !== b.foreplayId))} className="text-[#526074] hover:text-red-600">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm shrink-0">
            <label className="block">
              <span className="text-xs text-[#526074]">Format</span>
              <select
                className="mt-1 w-full border border-[#DCE3ED] rounded px-2 py-1.5 text-sm"
                value={filters.displayFormat}
                onChange={(e) => setFilters((f) => ({ ...f, displayFormat: e.target.value as ForeplayHarvestFilters["displayFormat"] }))}
              >
                <option value="image">Image</option>
                <option value="video">Video (stored as image stills — v1)</option>
                <option value="carousel">Carousel</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-[#526074]">Min days running</span>
              <input
                type="number" min={0} max={3650}
                className="mt-1 w-full border border-[#DCE3ED] rounded px-2 py-1.5 text-sm"
                value={filters.minDaysRunning ?? 0}
                onChange={(e) => setFilters((f) => ({ ...f, minDaysRunning: Math.max(0, Number(e.target.value) || 0) }))}
              />
            </label>
            <label className="block">
              <span className="text-xs text-[#526074]">Ads per brand (max 250)</span>
              <input
                type="number" min={1} max={250}
                data-testid="input-cap-per-brand"
                className="mt-1 w-full border border-[#DCE3ED] rounded px-2 py-1.5 text-sm"
                value={filters.capPerBrand}
                onChange={(e) => setFilters((f) => ({ ...f, capPerBrand: Math.min(250, Math.max(1, Number(e.target.value) || 1)) }))}
              />
            </label>
            <div className="flex flex-col justify-end gap-1.5">
              <label className="block">
                <span className="text-xs text-[#526074]">Sort</span>
                <select
                  data-testid="select-harvest-order"
                  className="mt-1 w-full border border-[#DCE3ED] rounded px-2 py-1.5 text-sm"
                  value={filters.order}
                  onChange={(e) => setFilters((f) => ({ ...f, order: e.target.value as ForeplayHarvestFilters["order"] }))}
                >
                  <option value="newest">Newest (fresh, survived min-days)</option>
                  <option value="longest_running">Longest running (evergreen)</option>
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={liveOnly} onChange={(e) => setLiveOnly(e.target.checked)} />
                Currently live only
              </label>
            </div>
          </div>

          {/* Cost bar + create */}
          <div className="flex items-center justify-between border-t border-[#DCE3ED] pt-3 shrink-0">
            <div className="text-sm">
              <span className="font-medium">{selected.length} brand{selected.length === 1 ? "" : "s"} × {filters.capPerBrand}</span>
              <span className="text-[#526074]"> · maximum ads to request: </span>
              <span className="font-semibold text-gray-900">{maxAds.toLocaleString()}</span>
            <div className="text-xs text-[#526074] mt-1">
                Foreplay checks credits when requests run. Maximum {maxAds.toLocaleString()} ads is a request ceiling, not a credit estimate.
              </div>
            </div>
            <Button
              data-testid="button-start-harvest"
              disabled={selected.length === 0 || creating}
              onClick={createJob}
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Create draft
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
